import { useState } from 'react';
import { ReactComponent as ExcelIcon } from '../../../assets/icons/excel.svg';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import Button from '../../../components/button';
import { apiService } from '../../../services/api.service';
import { TransactionFilters } from '../../../services/api.service-types';
import { errorService } from '../../../services/error.service';
import LoadingSpinner from '../../../components/loading-spinner'; 
import './transactions-page.part.filtered-data-item.scss';
import Skeleton from 'react-loading-skeleton';

interface TransactionsPartFilteredDataItemProps {
    total: number;
    noTransactions: number;
    filters: Partial<TransactionFilters>;
    dontShowPDF?: boolean;
    isLoading?: boolean;
    isAdmin?: boolean;
}

const TransactionsPartFilteredDataItem: React.FC<TransactionsPartFilteredDataItemProps> = ({
    total,
    noTransactions,
    filters,
    dontShowPDF = false,
    isLoading = false,
    isAdmin = false
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadType, setDownloadType] = useState<'excel' | 'pdf' | 'invoices' | null>(null);

    const handleDownload = async (fileType: 'excel' | 'pdf') => {
        try {
            setIsDownloading(true);
            setDownloadType(fileType);

            const response = fileType === 'excel'
                ? await apiService.generateExcel(filters)
                : await apiService.generatePdf(filters);
            console.log(response.data);

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileType === 'excel' ? 'transactions.xlsx' : 'transactions.pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error:any) {
            console.log(error.code );
            if(error.code == 'ECONNABORTED') {
                errorService.showError('Download failed', 'Please select a smaller interval');
            } else {
            errorService.showApiError(error);
        }
        } finally {
            setIsDownloading(false);
            setDownloadType(null);
        }
    };

    const handleBulkDownloadInvoices = async () => {
        try {
            setIsDownloading(true);
            setDownloadType('invoices');
            const response = await apiService.bulkDownloadInvoices(filters);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'invoices.zip');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error: any) {
            if (error.code == 'ECONNABORTED') {
                errorService.showError('Download failed', 'Please select a smaller interval');
            } else {
                errorService.showApiError(error);
            }
        } finally {
            setIsDownloading(false);
            setDownloadType(null);
        }
    };

    return (
        <div className="transactions-part-filtered-data-item flex-column flex-gap-20 flex-1">
            <div className="description">
                Number of Filtered Transactions: { isLoading ? <Skeleton width={50} highlightColor='#218579'/> : <span className="value">{noTransactions}</span>}
            </div>
            <div className="flex-row flex-align-space-between-center flex-gap-5">
                <Button
                    theme="accent"
                    label="Download Excel"
                    icon={ExcelIcon}
                    onClick={() => handleDownload('excel')}
                    disabled={isDownloading}
                />
                {isAdmin &&
                <Button
                    theme="accent"
                    label="Download Invoices"
                    icon={PdfIcon}
                    onClick={handleBulkDownloadInvoices}
                    disabled={isDownloading}
                />
                }
                {!dontShowPDF &&
                <Button
                    theme="accent"
                    label="Download PDF"
                    icon={PdfIcon}
                    onClick={() => handleDownload('pdf')}
                    disabled={isDownloading}
                />
                }
            </div>

            {isDownloading && (
                <div className="downloading-text flex-row flex-align-center">
                    <LoadingSpinner />
                    <span>Downloading {downloadType === 'excel' ? 'Excel' : downloadType === 'invoices' ? 'Invoices' : 'PDF'}...</span>
                </div>
            )}
        </div>
    );
};

export default TransactionsPartFilteredDataItem;
